import { Box, Collapse, Stack, Typography } from '@mui/material'
import { C1Alert, C1AlertTitle, C1Button, C1LoadingButton } from 'c1ui'
import { C1Dialog, C1DialogActions, C1DialogContent, C1DialogTitle } from 'components/core/dialog/C1Dialog'
import { APIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1FormLabel } from 'components/core/RHKFormComponents/C1FormLabel'
import { C1LabeledTextFieldWithPresets } from 'components/core/RHKFormComponents/C1LabeledTextFieldWithPresets'
import { SearchPolicies } from 'components/core/search/SearchPolicies'
import { SearchUsers } from 'components/core/search/users'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { policyTypeFromString } from 'components/pages/policy/type'
import { getApprovePresets, getCertifyPresets, getConfirmPresets, getDeclinePresets, getDenyPresets, getReassignPresets, getRemovePresets } from 'components/pages/task/action/messages'
import { TaskValue } from 'lib/actions/task'
import { TaskTypeString } from 'lib/constants/task'
import { Info, Minus, Plus } from 'lucide-react'
import { Policy } from 'pbts/c1/api/policy/v1/policy'
import { ChangePolicyActionArgs, TaskActionsServiceBulkRequest } from 'pbts/c1/api/task/v1/actions'
import { TaskSearchRequest } from 'pbts/c1/api/task/v1/search'
import { TaskActionType } from 'pbts/c1/api/task/v1/task'
import { User } from 'pbts/c1/api/user/v1/user'
import React, { FC, useCallback, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { usePendingTicketActionTracker } from 'redux_toolkit/lib/PendingTicketActionTracker'
import { BulkActionStatus, SelectionSliceKeys, useSelections, useSelectionsState } from 'redux_toolkit/slices/selectionsSlice'
import { bulkActionApi } from 'redux_toolkit/tasks'

type TaskBulkActionDialogProps = {
    actionType: TaskActionType
    titleText: string
    descriptionText: string
    submitButtonText: string
    commentFieldLabelText: React.ReactNode
    actionArgs?: TaskActionsServiceBulkRequest['actionArgs']
    color?: 'primary' | 'warning' | 'success' | 'error'
    children?: React.ReactNode
    presets?: string[]
    hasValidationError?: boolean
    commentRequired?: boolean
    commentRequiredMessage?: string
    taskSearchRequest?: TaskSearchRequest
    isSelectingViaFilter?: boolean
    accessReviewId?: string
    isPendingAction?: boolean
} & ClosableProps

type TaskBulkActionDialogWrapperProps = {
    accessReviewId?: string
    taskSearchRequest?: TaskSearchRequest
    taskTypesSet: Set<TaskTypeString>
    isSelectingViaFilter?: boolean
    createPendingActions?: boolean
} & ClosableProps

export const TaskBulkActionDialog: FC<TaskBulkActionDialogProps> = ({
    actionType,
    actionArgs,
    titleText,
    descriptionText,
    color,
    submitButtonText,
    commentFieldLabelText,
    presets,
    children,
    hasValidationError,
    close,
    commentRequired = true,
    commentRequiredMessage,
    taskSearchRequest,
    isSelectingViaFilter,
    accessReviewId = '',
    isPendingAction = false,
}) => {
    const dispatch = useDispatch()
    const intl = useIntl()
    const [comment, setComment] = useState('')
    const [helperTextError, setHelperTextError] = useState<string | undefined>()
    const [alertError, setAlertError] = useState<string | undefined>()
    const [isReasonFieldExpanded, setIsReasonFieldExpanded] = useState(false)
    const { mutate: bulkAction, inProgress, error } = bulkActionApi()
    const pendingTicketActionTracker = usePendingTicketActionTracker()

    const showCommentCollapse = !commentRequired

    const {
        actions: { setActiveBulkActionId, setBulkActionStatus, clearSelections },
    } = useSelections<TaskValue>(SelectionSliceKeys.TASKS)

    const { selections } = useSelectionsState<TaskValue>(SelectionSliceKeys.TASKS)

    const onComment = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
        setComment(e.currentTarget.value)
    }, [])

    const commentTextField = useMemo((): React.ReactNode => <C1LabeledTextFieldWithPresets
        presets={presets}
        value={comment}
        setValue={setComment}
        margin="dense"
        name="comment"
        id="comment"
        fullWidth
        label={<Stack direction="row" gap={1} alignItems="flex-end">
            {commentFieldLabelText}
            {!commentRequired && <Typography variant="caption">
                <FormattedMessage
                    id="bulk_selected_bulk_selected_action_reason_optional"
                    defaultMessage="(optional)"
                />
            </Typography>}
        </Stack>}
        onChange={onComment}
        autoFocus
        onFocus={(): void => {
            setHelperTextError(undefined)
        }}
        error={!!helperTextError}
        helperText={helperTextError ||
            ((!commentRequired && !comment) && <Stack direction="row" spacing={1} mt={1}>
                <C1Icon icon={Info} />
                <Typography component="span">
                    {commentRequiredMessage
                        || <FormattedMessage id="bulk_selected_certify_form_reason_tooltip" defaultMessage="If left blank, this action will fail for any tasks requiring a justification." />}
                </Typography>
            </Stack>)
        }
    />, [presets, comment, setComment, commentFieldLabelText, commentRequired, onComment, helperTextError, commentRequiredMessage])

    const onSubmit = async (e): Promise<void> => {
        e.preventDefault()

        if ((!comment || comment === '') && commentRequired) {
            setHelperTextError(intl.formatMessage({
                id: 'bulk_selected_bulk_selected_action_reason_required',
                defaultMessage: 'A reason is required',
            }))
            return
        }

        if (hasValidationError) {
            return
        }

        try {
            let searchRequest: TaskSearchRequest

            if (isSelectingViaFilter && taskSearchRequest) {
                // Use the full filter search request
                searchRequest = TaskSearchRequest.create(taskSearchRequest)
            } else {
                // Use individual task IDs
                const ids = selections.map((t) => t.id)
                if (ids.length === 0) {
                    setHelperTextError(intl.formatMessage({
                        id: 'bulk_selected_bulk_selected_action_no_tasks_selected',
                        defaultMessage: 'No tasks selected',
                    }))
                    return
                }
                searchRequest = TaskSearchRequest.create({
                    refs: ids.map((id) => ({ id })),
                    accessReviewIds: taskSearchRequest?.accessReviewIds,
                })
            }

            const response = await bulkAction(TaskActionsServiceBulkRequest.create({
                comment,
                actionType,
                searchRequest,
                actionArgs: actionArgs || undefined,
            }))

            const bulkActionId = response.data?.bulkActionId
            if (bulkActionId) {
                dispatch(setActiveBulkActionId(bulkActionId))
                dispatch(setBulkActionStatus(BulkActionStatus.ACTIVE))
                dispatch(clearSelections())
            }

            if (isPendingAction && accessReviewId && pendingTicketActionTracker && bulkActionId) {
                pendingTicketActionTracker.trackBulkActionById(bulkActionId, accessReviewId)
            }

            closeDialog()
        } catch (err) {
            // error is handled by the APIError component & createBulkActionApi
        }

        setAlertError(undefined)
        setHelperTextError(undefined)
    }

    const closeDialog = (): void => {
        setComment('')
        setAlertError(undefined)
        setHelperTextError(undefined)
        close()
    }

    return <C1Dialog
        open
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        sx={{ '& .MuiFormControl-root': { mt: 0 } }}
        color={color || 'primary'}
    >
        <form onSubmit={onSubmit}>
            <C1DialogTitle>
                {titleText}
            </C1DialogTitle>
            <C1DialogContent>
                <Stack spacing={4} mx={1}>
                    <APIError
                        title={<FormattedMessage id="bulk_selected_bulk_selected_action_error" defaultMessage="Error performing bulk action" />}
                        err={error}
                    />
                    {alertError && <C1Alert severity="error">
                        <C1AlertTitle>{alertError}</C1AlertTitle>
                    </C1Alert>}
                    <Typography>{descriptionText}</Typography>

                    {children}

                    {showCommentCollapse
                        ? <Stack spacing={1}>
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                sx={{ cursor: 'pointer' }}
                                onClick={(): void => setIsReasonFieldExpanded(!isReasonFieldExpanded)}
                            >
                                {isReasonFieldExpanded ? <C1Icon icon={Minus} sx={{ color: 'primary.main' }} /> : <C1Icon icon={Plus} sx={{ color: 'primary.main' }} />}
                                <Typography variant="subtitle1" color="primary.main">
                                    {isReasonFieldExpanded
                                        ? <FormattedMessage
                                            id="bulk_selected_bulk_selected_action_hide_comment"
                                            defaultMessage="Hide comment"
                                        />
                                        : <FormattedMessage
                                            id="bulk_selected_bulk_selected_action_add_comment"
                                            defaultMessage="Add comment"
                                        />}
                                </Typography>
                            </Box>
                            <Collapse in={isReasonFieldExpanded} timeout={300}>
                                {commentTextField}
                            </Collapse>
                        </Stack>
                        : commentTextField
                    }
                </Stack>
            </C1DialogContent>
            <C1DialogActions>
                <C1Button onClick={closeDialog} variant="outlined">
                    <FormattedMessage id="bulk_selected_bulk_selected_action_go_back" defaultMessage="Go back" />
                </C1Button>
                <C1LoadingButton color={color === 'error' ? 'error' : 'primary'} type="submit" loading={inProgress} variant="contained">
                    {submitButtonText}
                </C1LoadingButton>
            </C1DialogActions>
        </form>
    </C1Dialog>
}

type ErrorDialogProps = {
    title: string
    color?: 'primary' | 'warning' | 'success' | 'error'
    message: string
    children?: React.ReactNode
} & ClosableProps

export const TaskErrorDialog: FC<ErrorDialogProps> = ({ title, color, message, children, close }) => {
    return <C1Dialog
        open
        onClose={close}
        fullWidth
        maxWidth="sm"
        sx={{
            '& .MuiFormControl-root': {
                mt: 0,
            },
        }}
    >
        <C1DialogTitle color={color || 'primary'}>{title}</C1DialogTitle>
        <C1DialogContent>
            <Box mb={4}>
                <Typography>{message}</Typography>
            </Box>

            <Box mb={4}>
                {children}
            </Box>

        </C1DialogContent>
        <C1DialogActions>
            <C1Button onClick={close} variant="outlined">
                <FormattedMessage id="bulk_selected_bulk_selected_action_go_back" defaultMessage="Go back" />
            </C1Button>
        </C1DialogActions>
    </C1Dialog>
}

export const TaskBulkActionCancelDialog: FC<TaskBulkActionDialogWrapperProps> = ({
    close, taskSearchRequest, isSelectingViaFilter,
}) => {
    const intl = useIntl()
    return <TaskBulkActionDialog
        close={close}
        taskSearchRequest={taskSearchRequest}
        isSelectingViaFilter={isSelectingViaFilter}
        color="warning"
        actionType={TaskActionType.CLOSE}
        titleText={intl.formatMessage({
            id: 'bulk_selected_cancel_form_title',
            defaultMessage: 'Cancel tasks',
        })}
        descriptionText={intl.formatMessage({
            id: 'bulk_selected_cancel_form_description',
            defaultMessage: 'Canceling stops all activity in progress but does not delete the tasks.',
        })}
        commentFieldLabelText={intl.formatMessage({
            id: 'bulk_selected_cancel_form_description',
            defaultMessage: 'Reason for canceling',
        })}
        submitButtonText={intl.formatMessage({
            id: 'bulk_selected_cancel_form_description',
            defaultMessage: 'Cancel tasks',
        })}
    />
}

export const TaskBulkActionCommentDialog: FC<TaskBulkActionDialogWrapperProps> = ({
    close, taskSearchRequest, isSelectingViaFilter,
}) => {
    const intl = useIntl()
    return <TaskBulkActionDialog
        close={close}
        taskSearchRequest={taskSearchRequest}
        isSelectingViaFilter={isSelectingViaFilter}
        actionType={TaskActionType.COMMENT}
        titleText={intl.formatMessage({
            id: 'bulk_selected_comment_form_title',
            defaultMessage: 'Comment on tasks',
        })}
        descriptionText={intl.formatMessage({
            id: 'bulk_selected_comment_form_description',
            defaultMessage: 'Comments will be added to the selected tasks.',
        })}
        commentFieldLabelText={intl.formatMessage({
            id: 'bulk_selected_comment_form_description',
            defaultMessage: 'Comment',
        })}
        submitButtonText={intl.formatMessage({
            id: 'bulk_selected_comment_form_cta',
            defaultMessage: 'Comment',
        })}
    />
}

export const TaskBulkActionRestartDialog: FC<TaskBulkActionDialogWrapperProps> = ({
    close, taskSearchRequest, isSelectingViaFilter,
}) => {
    const intl = useIntl()
    return <TaskBulkActionDialog
        close={close}
        taskSearchRequest={taskSearchRequest}
        isSelectingViaFilter={isSelectingViaFilter}
        actionType={TaskActionType.RESTART}
        titleText={intl.formatMessage({
            id: 'bulk_selected_restart_form_title',
            defaultMessage: 'Restart tasks',
        })}
        descriptionText={intl.formatMessage({
            id: 'bulk_selected_restart_form_description',
            defaultMessage: 'Restarting tasks restores them to their original state. Any completed changes, decisions, or actions will be cleared.',
        })}
        commentFieldLabelText={intl.formatMessage({
            id: 'bulk_selected_restart_form_reason_label',
            defaultMessage: 'Reason for restarting',
        })}
        submitButtonText={intl.formatMessage({
            id: 'bulk_selected_restart_form_cta',
            defaultMessage: 'Restart',
        })}
    />
}

// Depending on the type of the selected tasks, we need to show different text
export const TaskBulkActionApproveDialog: FC<TaskBulkActionDialogWrapperProps> = ({
    close, taskSearchRequest, taskTypesSet, isSelectingViaFilter, createPendingActions = false,
    accessReviewId,
}) => {
    const intl = useIntl()

    if (taskTypesSet.size !== 1) {
        return <TaskErrorDialog
            close={close}
            title={intl.formatMessage({ defaultMessage: 'Unable to approve tasks', id: 'bulk_selected_bulk_selected_approve_error_title' })}
            message={intl.formatMessage({ defaultMessage: "We can't approve tasks because you've selected tasks that are of more than one type.", id: 'bulk_selected_approve_error_multiple_types' })}
        >
            <FormattedMessage
                id="bulk_selected_approve_deny_error_multiple_types_description"
                defaultMessage="Please refine your selection to exclusively grant, review, or revoke tasks."
            />
        </TaskErrorDialog>
    }

    if (taskTypesSet.has(TaskTypeString.Certify)) {
        return <TaskBulkActionDialog
            close={close}
            taskSearchRequest={taskSearchRequest}
            isSelectingViaFilter={isSelectingViaFilter}
            actionType={TaskActionType.APPROVE}
            titleText={intl.formatMessage({
                id: 'bulk_selected_certify_form_title',
                defaultMessage: 'Certify reviews',
            })}
            descriptionText={intl.formatMessage({
                id: 'bulk_selected_certify_form_description',
                defaultMessage: 'Certify the access in the selected tasks and move each review task on to its next step.',
            })}
            commentFieldLabelText={
                intl.formatMessage({
                    id: 'bulk_selected_certify_form_reason_label',
                    defaultMessage: 'Reason for certifying',
                })
            }
            submitButtonText={intl.formatMessage({
                id: 'bulk_selected_certify_form_cta',
                defaultMessage: 'Certify',
            })}
            presets={getCertifyPresets(intl)}
            commentRequired={false}
            commentRequiredMessage={intl.formatMessage({
                id: 'bulk_selected_certify_form_reason_tooltip',
                defaultMessage: 'If left blank, certification will fail on any reviews that require a reason.',
            })}
            actionArgs={createPendingActions ? {
                oneofKind: 'certify',
                certify: {
                    createPendingActions: true,
                },
            } : undefined}
            accessReviewId={accessReviewId}
            isPendingAction={createPendingActions}
        />
    }

    if (taskTypesSet.has(TaskTypeString.Grant)) {
        return <TaskBulkActionDialog
            close={close}
            taskSearchRequest={taskSearchRequest}
            isSelectingViaFilter={isSelectingViaFilter}
            actionType={TaskActionType.APPROVE}
            titleText={intl.formatMessage({
                id: 'bulk_selected_approve_form_title',
                defaultMessage: 'Approve requests',
            })}
            descriptionText={intl.formatMessage({
                id: 'bulk_selected_approving_form_description',
                defaultMessage: 'Approve the access requested in the selected tasks and move each task on to its next step.',
            })}
            commentFieldLabelText={intl.formatMessage({
                id: 'bulk_selected_approve_form_reason_label',
                defaultMessage: 'Reason for approving',
            })}
            submitButtonText={intl.formatMessage({
                id: 'bulk_selected_approve_form_cta',
                defaultMessage: 'Approve',
            })}
            presets={getApprovePresets(intl)}
            commentRequired={false}
            commentRequiredMessage={intl.formatMessage({
                id: 'bulk_selected_approve_form_reason_tooltip',
                defaultMessage: 'If left blank, approval will fail on any requests that require a reason.',
            })}
        />
    }

    if (taskTypesSet.has(TaskTypeString.Revoke)) {
        return <TaskBulkActionDialog
            close={close}
            taskSearchRequest={taskSearchRequest}
            isSelectingViaFilter={isSelectingViaFilter}
            actionType={TaskActionType.APPROVE}
            titleText={intl.formatMessage({
                id: 'bulk_selected_confirm_form_title',
                defaultMessage: 'Confirm revocations',
            })}
            descriptionText={intl.formatMessage({
                id: 'bulk_selected_confirm_form_description',
                defaultMessage: 'Confirm the revocation proposed in the selected tasks and move each task on to its next step.',
            })}
            commentFieldLabelText={intl.formatMessage({
                id: 'bulk_selected_confirm_form_reason_label',
                defaultMessage: 'Reason for confirming',
            })}
            submitButtonText={intl.formatMessage({
                id: 'bulk_selected_approve_form_cta',
                defaultMessage: 'Confirm',
            })}
            presets={getConfirmPresets(intl)}
            commentRequired={false}
            commentRequiredMessage={intl.formatMessage({
                id: 'bulk_selected_confirm_form_reason_tooltip',
                defaultMessage: 'If left blank, confirmation will fail on any revocations that require a reason.',
            })}
        />
    }

    // Otherwise you've somehow selected an invalid task type
    return null
}

// Depending on the type of the selected tasks, we need to show different text
export const TaskBulkActionDenyDialog: FC<TaskBulkActionDialogWrapperProps> = ({
    close, taskSearchRequest, taskTypesSet, isSelectingViaFilter, createPendingActions = false,
    accessReviewId,
}) => {
    const intl = useIntl()

    if (taskTypesSet.size !== 1) {
        return <TaskErrorDialog
            close={close}
            title={intl.formatMessage({ defaultMessage: 'Unable to deny tasks', id: 'bulk_selected_bulk_selected_deny_error_title' })}
            message={intl.formatMessage({ defaultMessage: "We can't deny tasks because you've selected tasks that are of more than one type.", id: 'bulk_selected_deny_error_multiple_types' })}
        >
            <FormattedMessage
                id="bulk_selected_approve_deny_error_multiple_types_description"
                defaultMessage="Please refine your selection to exclusively grant, review, or revoke tasks."
            />
        </TaskErrorDialog>
    }

    if (taskTypesSet.has(TaskTypeString.Certify)) {
        return <TaskBulkActionDialog
            close={close}
            taskSearchRequest={taskSearchRequest}
            isSelectingViaFilter={isSelectingViaFilter}
            actionType={TaskActionType.DENY}
            titleText={intl.formatMessage({
                id: 'bulk_selected_remove_form_title',
                defaultMessage: 'Deny reviews',
            })}
            descriptionText={intl.formatMessage({
                id: 'bulk_selected_remove_form_description',
                defaultMessage: 'Deny the selected reviews. Revocation processes will begin when the campaign is complete, if your campaign is configured to do so.',
            })}
            commentFieldLabelText={intl.formatMessage({
                id: 'bulk_selected_remove_form_reason_label',
                defaultMessage: 'Reason for denial',
            })}
            submitButtonText={intl.formatMessage({
                id: 'bulk_selected_remove_form_cta',
                defaultMessage: 'Deny',
            })}
            presets={getRemovePresets(intl)}
            commentRequired={false}
            commentRequiredMessage={intl.formatMessage({
                id: 'bulk_selected_remove_form_reason_tooltip',
                defaultMessage: 'If left blank, denial will fail on any reviews that require a reason.',
            })}
            actionArgs={createPendingActions ? {
                oneofKind: 'certify',
                certify: {
                    createPendingActions: true,
                },
            } : undefined}
            accessReviewId={accessReviewId}
            isPendingAction={createPendingActions}
        />
    }

    if (taskTypesSet.has(TaskTypeString.Grant)) {
        return <TaskBulkActionDialog
            close={close}
            taskSearchRequest={taskSearchRequest}
            isSelectingViaFilter={isSelectingViaFilter}
            actionType={TaskActionType.DENY}
            titleText={intl.formatMessage({
                id: 'bulk_selected_deny_form_title',
                defaultMessage: 'Deny requests',
            })}
            descriptionText={intl.formatMessage({
                id: 'bulk_selected_certify_form_description',
                defaultMessage: 'Deny the access requested in the selected tasks and close each task.',
            })}
            commentFieldLabelText={intl.formatMessage({
                id: 'bulk_selected_deny_form_reason_label',
                defaultMessage: 'Reason for denial',
            })}
            submitButtonText={intl.formatMessage({
                id: 'bulk_selected_deny_form_cta',
                defaultMessage: 'Deny',
            })}
            presets={getDenyPresets(intl)}
            commentRequired={false}
            commentRequiredMessage={intl.formatMessage({
                id: 'bulk_selected_deny_form_reason_tooltip',
                defaultMessage: 'If left blank, denial will fail on any requests that require a reason.',
            })}
        />
    }

    if (taskTypesSet.has(TaskTypeString.Revoke)) {
        return <TaskBulkActionDialog
            close={close}
            taskSearchRequest={taskSearchRequest}
            isSelectingViaFilter={isSelectingViaFilter}
            actionType={TaskActionType.DENY}
            titleText={intl.formatMessage({
                id: 'bulk_selected_decline_form_title',
                defaultMessage: 'Decline revocations',
            })}
            descriptionText={intl.formatMessage({
                id: 'bulk_selected_certify_form_description',
                defaultMessage: 'Decline the revocation proposed in the selected tasks and close each task.',
            })}
            commentFieldLabelText={intl.formatMessage({
                id: 'bulk_selected_decline_form_reason_label',
                defaultMessage: 'Reason for declining',
            })}
            submitButtonText={intl.formatMessage({
                id: 'bulk_selected_decline_form_cta',
                defaultMessage: 'Decline',
            })}
            presets={getDeclinePresets(intl)}
            commentRequired={false}
            commentRequiredMessage={intl.formatMessage({
                id: 'bulk_selected_decline_form_reason_tooltip',
                defaultMessage: 'If left blank, declining will fail on any revocations that require a reason.',
            })}
        />
    }

    // Otherwise you've somehow selected an invalid task type
    return null
}

export const TaskBulkActionReassignDialog: FC<TaskBulkActionDialogWrapperProps> = ({
    close, taskSearchRequest, isSelectingViaFilter,
}) => {
    const intl = useIntl()
    const [users, setUsers] = useState<User[]>([])
    const [userHelperTextError, setUserHelperTextError] = useState<string>()

    const actionArgs = useMemo(() => (users.length === 0 ? undefined : {
        oneofKind: 'reassign' as const,
        reassign: { newUserIds: users.map((u) => u.id) },
    }), [users])

    const hasValidationError = useMemo(() => {
        return users.length === 0
    }, [users])

    return <TaskBulkActionDialog
        close={close}
        taskSearchRequest={taskSearchRequest}
        isSelectingViaFilter={isSelectingViaFilter}
        actionArgs={actionArgs}
        actionType={TaskActionType.REASSIGN}
        titleText={intl.formatMessage({
            id: 'bulk_selected_reassign_form_title',
            defaultMessage: 'Reassign tasks',
        })}
        descriptionText={intl.formatMessage({
            id: 'bulk_selected_reassign_form_description',
            defaultMessage: 'Assign these tasks to a different user. ',
        })}
        commentFieldLabelText={intl.formatMessage({
            id: 'bulk_selected_reassign_form_description',
            defaultMessage: 'Reason for reassignment',
        })}
        submitButtonText={intl.formatMessage({
            id: 'bulk_selected_reassign_form_description',
            defaultMessage: 'Reassign',
        })}
        presets={getReassignPresets(intl)}
        hasValidationError={hasValidationError}
    >
        <SearchUsers
            label={intl.formatMessage({
                id: 'bulk_selected_reassign_action_user_pick_label',
                defaultMessage: 'Select assignee',
            })}
            autoFocus
            onSelected={(value): void => setUsers(value)}
            onBlur={(): void => {
                if (users.length === 0) {
                    setUserHelperTextError(intl.formatMessage({
                        id: 'bulk_selected_reassign_action_user_pick_label',
                        defaultMessage: 'Select assignee',
                    }))
                }
            }}
            onFocus={(): void => {
                setUserHelperTextError(undefined)
            }}
            error={!!userHelperTextError}
            helperText={userHelperTextError}
        />
    </TaskBulkActionDialog>
}

export const TaskBulkActionChangePolicyDialog: FC<TaskBulkActionDialogWrapperProps> = ({
    close, taskSearchRequest, taskTypesSet, isSelectingViaFilter,
}) => {
    const intl = useIntl()
    const [policy, setPolicy] = useState<Policy>()

    const actionArgs = useMemo(() => (!policy ? undefined : {
        oneofKind: 'changePolicy' as const,
        changePolicy: ChangePolicyActionArgs.create({
            newPolicyId: policy?.id,
        }),
    }), [policy])

    if (taskTypesSet.size === 0 || taskTypesSet.size > 1 || taskTypesSet.has(TaskTypeString.Grant)) {
        return <TaskErrorDialog
            close={close}
            title={intl.formatMessage({ defaultMessage: 'Unable to change policy', id: 'bulk_selected_change_policy_error_title' })}
            message={intl.formatMessage(taskTypesSet.has(TaskTypeString.Grant)
                ? { defaultMessage: 'We cannot change the policy of request tasks.', id: 'bulk_selected_change_policy_error_no_grants' }
                : { defaultMessage: "We cannot change the policy because you've selected more than one type of task.", id: 'bulk_selected_change_policy_error_multiple_types' })}
        >
            <FormattedMessage
                id="bulk_selected_change_policy_user_pick_label"
                defaultMessage="Please refine your selection to only reviews or revocations."
            />
        </TaskErrorDialog>
    }

    const singlePolicyType = policyTypeFromString(taskTypesSet.values().next().value?.toLowerCase())

    // I dont know how this case could happen, but we until we get error reports, we should handle it
    if (!singlePolicyType) {
        return <TaskErrorDialog
            close={close}
            title={intl.formatMessage({ defaultMessage: 'Unable to change policy', id: 'bulk_selected_change_policy_error_title' })}
            message={intl.formatMessage({ defaultMessage: 'We cannot find policies avaliable with that task.', id: 'bulk_selected_change_policy_error_no_policies' })}
        >
            <FormattedMessage
                id="bulk_selected_change_policy_user_pick_label"
                defaultMessage="Please try your search again later."
            />
        </TaskErrorDialog>
    }

    return <TaskBulkActionDialog
        close={close}
        taskSearchRequest={taskSearchRequest}
        isSelectingViaFilter={isSelectingViaFilter}
        actionArgs={actionArgs}
        actionType={TaskActionType.CHANGE_POLICY}
        titleText={intl.formatMessage({
            id: 'bulk_selected_change_policy_form_title',
            defaultMessage: 'Change policy',
        })}
        descriptionText={intl.formatMessage({
            id: 'bulk_selected_change_policy_form_description',
            defaultMessage: 'Changing the policy also restarts the impacted tasks.',
        })}
        commentFieldLabelText={intl.formatMessage({
            id: 'bulk_selected_change_policy_form_description',
            defaultMessage: 'Reason for changing policy',
        })}
        submitButtonText={intl.formatMessage({
            id: 'bulk_selected_change_policy_form_description',
            defaultMessage: 'Change',
        })}
    >
        <Box mb={1}>
            <C1FormLabel label={<FormattedMessage id="bulk_selected_change_policy_user_pick_label" defaultMessage="Select a new policy" />} />
        </Box>
        <SearchPolicies
            autoFocus
            onSelected={(value): void => setPolicy(value)}
            policyType={singlePolicyType}
        />
    </TaskBulkActionDialog>
}

export const TaskBulkActionHardResetDialog: FC<TaskBulkActionDialogWrapperProps> = ({
    close, taskSearchRequest, isSelectingViaFilter,
}) => {
    const intl = useIntl()
    return <TaskBulkActionDialog
        close={close}
        taskSearchRequest={taskSearchRequest}
        isSelectingViaFilter={isSelectingViaFilter}
        color="error"
        actionType={TaskActionType.HARD_RESET}
        titleText={intl.formatMessage({
            id: 'bulk_selected_hard_reset_form_title',
            defaultMessage: 'Hard reset tasks',
        })}
        descriptionText={intl.formatMessage({
            id: 'bulk_selected_hard_reset_form_description',
            defaultMessage: 'Hard resetting tasks restores them to their original state. Any completed changes, decisions, or actions will be cleared.',
        })}
        commentFieldLabelText={intl.formatMessage({
            id: 'bulk_selected_hard_reset_form_description',
            defaultMessage: 'Reason for hard resetting',
        })}
        submitButtonText={intl.formatMessage({
            id: 'bulk_selected_hard_reset_form_description',
            defaultMessage: 'Hard reset',
        })}
    >
        <FormattedMessage
            id="bulk_selected_hard_reset_action_user_pick_label"
            defaultMessage="If the policies governing any of the selected tasks have changed, hard resetting also applies the new policies to the tasks."
        />
    </TaskBulkActionDialog>
}

// This also serves marking a revoke task as deprovisioned
export const TaskBulkActionProvisionCompleteDialog: FC<TaskBulkActionDialogWrapperProps> = ({
    close, taskSearchRequest, taskTypesSet, isSelectingViaFilter,
}) => {
    const intl = useIntl()

    if (taskTypesSet.has(TaskTypeString.Grant)) {
        return <TaskBulkActionDialog
            close={close}
            taskSearchRequest={taskSearchRequest}
            isSelectingViaFilter={isSelectingViaFilter}
            actionType={TaskActionType.PROVISION_COMPLETE}
            titleText={intl.formatMessage({
                id: 'bulk_selected_provision_complete_form_title',
                defaultMessage: 'Mark tasks as provisioned',
            })}
            descriptionText={intl.formatMessage({
                id: 'bulk_selected_provision_complete_form_description',
                defaultMessage: 'This will mark the selected tasks as successfully provisioned.',
            })}
            commentFieldLabelText={intl.formatMessage({
                id: 'bulk_selected_provision_complete_form_reason_label',
                defaultMessage: 'Reason for marking as provisioned',
            })}
            submitButtonText={intl.formatMessage({
                id: 'bulk_selected_provision_complete_form_cta',
                defaultMessage: 'Mark as provisioned',
            })}
        />
    }

    // Its a revoke task
    return <TaskBulkActionDialog
        close={close}
        taskSearchRequest={taskSearchRequest}
        isSelectingViaFilter={isSelectingViaFilter}
        actionType={TaskActionType.PROVISION_COMPLETE}
        titleText={intl.formatMessage({
            id: 'bulk_selected_deprovision_complete_form_title',
            defaultMessage: 'Mark tasks as deprovisioned',
        })}
        descriptionText={intl.formatMessage({
            id: 'bulk_selected_deprovision_complete_form_description',
            defaultMessage: 'This will mark the selected tasks as successfully deprovisioned.',
        })}
        commentFieldLabelText={intl.formatMessage({
            id: 'bulk_selected_deprovision_complete_form_reason_label',
            defaultMessage: 'Reason for marking as deprovisioned',
        })}
        submitButtonText={intl.formatMessage({
            id: 'bulk_selected_deprovision_complete_form_cta',
            defaultMessage: 'Mark as deprovisioned',
        })}
    />
}

export const TaskBulkActionSkipStepDialog: FC<TaskBulkActionDialogWrapperProps> = ({
    close, taskSearchRequest, isSelectingViaFilter,
}) => {
    const intl = useIntl()

    return <TaskBulkActionDialog
        close={close}
        taskSearchRequest={taskSearchRequest}
        isSelectingViaFilter={isSelectingViaFilter}
        actionType={TaskActionType.SKIP_STEP}
        titleText={intl.formatMessage({
            id: 'bulk_selected_skip_step_form_title',
            defaultMessage: 'Skip task steps',
        })}
        descriptionText={intl.formatMessage({
            id: 'bulk_selected_skip_step_form_description',
            defaultMessage: 'This will skip the current policy step for the selected tasks.',
        })}
        commentFieldLabelText={intl.formatMessage({
            id: 'bulk_selected_skip_step_form_reason_label',
            defaultMessage: 'Reason for skipping',
        })}
        submitButtonText={intl.formatMessage({
            id: 'bulk_selected_skip_step_form_cta',
            defaultMessage: 'Skip step',
        })}
    />
}
